<script setup lang="ts">
import { computed } from 'vue'
import { ElCheckboxGroup, ElCheckbox } from 'element-plus'

import { HintIcon } from '@/shared/ui/HintIcon'

/**
 * A set of boxes over one list, modelling the array of what is ticked — pass the `options`
 * and never `v-for` a `Checkbox` to build one, which leaves the group without its
 * own state.
 *
 * The boxes are laid out in `columns` columns (two by default, capped at three on a narrow
 * screen), so a long list stays readable instead of running down the page. An option may
 * carry a `hint`, which is drawn as a `?` beside its label.
 *
 * A field bound to numeric ids goes through `useNumericIds`: what comes back out is
 * whatever the options carried, typed `string | number`.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: Array<string | number>
    options: Array<{
      label: string
      value: string | number
      hint?: string
    }>
    disabled?: boolean
    /** Number of columns in the grid (0 = no grid, use default flow) */
    columns?: number
  }>(),
  { columns: 2 }
)

const responsiveColumns = computed(() => Math.min(props.columns, 3))

const emit = defineEmits<{
  (e: 'update:modelValue', v: Array<string | number>): void
}>()
</script>

<template>
  <ElCheckboxGroup
    :model-value="modelValue"
    :class="{ 'checkbox-group--grid': columns > 0 }"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <ElCheckbox
      v-for="opt in options"
      :key="String(opt.value)"
      :value="opt.value"
      :disabled="disabled"
    >
      {{ opt.label }}
      <!-- .stop so reading the hint does not toggle the checkbox it belongs to -->
      <span v-if="opt.hint" @click.stop.prevent>
        <HintIcon :content="opt.hint" />
      </span>
    </ElCheckbox>
  </ElCheckboxGroup>
</template>

<style lang="scss" scoped>
.checkbox-group--grid {
  display: grid;
  grid-template-columns: repeat(v-bind(columns), 1fr);
  gap: 8px 24px;
  align-items: center;
}

@media (max-width: $bp-narrow) {
  .checkbox-group--grid {
    grid-template-columns: repeat(v-bind(responsiveColumns), 1fr);
  }
}
</style>
